import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import { ColorSwatchDto } from './color-palette.dto';

/**
 * The company colour palette — `GET /api/v1/color-palette`.
 *
 * Top level rather than under a module, because one palette serves the whole
 * company: a path naming one module invites a second palette the day another
 * module wants swatches (`backend-spec-weekly-schedule.md` §4). Any signed-in
 * caller may read it; it deliberately has no access-matrix resource, so a colour
 * picker is never the thing that 403s.
 *
 * `available()` reports whether an API is configured at all — with an empty
 * `apiUrl` the app runs on seeded data, and callers check before reaching for
 * the network.
 */
@Injectable({ providedIn: 'root' })
export class ColorPaletteApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = no API yet. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  /**
   * The swatches, in the order the company authored them — the order every
   * picker shows them in (`docs/backend-request-core-palette.md` §2). Never
   * re-sorted here. Read once for the whole app, by `ReferenceEffects`.
   */
  list(): Observable<ColorSwatchDto[]> {
    // `apiUrl` already carries the /api/v1 prefix — appending another produced
    // /api/v1/api/v1/… and a 404 against every Company endpoint.
    return this.http.get<ColorSwatchDto[]>(`${this.config.apiUrl.replace(/\/$/, '')}/color-palette`);
  }
}
